<template>
  <div
    :id="id"
    class="element qrcode-widget draggable"
    :class="[
      cssClasses,
      animationInStyle,
      animationOutStyle,
      { selected: isSelected, locked },
    ]"
    :style="{ '--dot-color': dotColor,'--background-color': backgroundColor, ...basicStyles }"
  >
    <transition
      :enter-active-class="'animated ' + animationIn"
      :leave-active-class="'animated ' + animationOut"
      :duration="{ leave: leaveDuration }"
      mode=""
      @after-enter="afterEnter"
      @before-leave="beforeLeave"
    >
      <div
        v-show="toggle"
        key="qrcode"
        ref="qrcode"
        class="qr-code"
        :class="[loopingClass]"
        :style="[animationStyles]"
        :data-test-qr-logo="element.hasLogo ? logoUrl : undefined"
        :data-test-qr-dot-color="dotColor"
        :data-test-qr-background-color="backgroundColor"
        :data-test-qr-permalink="permalink"
      ></div>
    </transition>
  </div>
</template>

<script>
import { throttle } from 'lodash-es';
// Vite pre-bundles this legacy UMD script through the alias in vite.config.mjs.
// eslint-disable-next-line import/no-unresolved
import EasyQRCode from 'easy-qrcode-premium';
import EventBus from '../../../utils/event-bus';
import ElementsMixins from '../../../mixins/elements-mixins';
import ResizeMixins from '../../../mixins/resize-mixins';
import MoveMixins from '../../../mixins/move-mixins';

// Very simple. Optional https/mailto protocols, at least two character domain extension.
const urlRegex = /(https?:\/\/|mailto:)?[^\s\\]+\.[^\s\\]{2,}/;

const isURL = (url) => (
  url
  && urlRegex.test(url)
);

export default {
  name: 'QRCodeElement',
  mixins: [ElementsMixins, ResizeMixins, MoveMixins],
  data() {
    return {
      qrcode: null,
    };
  },
  computed: {
    dotColor() {
      return this.element.dotColor;
    },

    backgroundColor() {
      return this.element.backgroundColor;
    },

    permalink() {
      if (!this.isMapped('permalink') && this.element.trackingLinkRid) {
        const baseUrl = process.env.TGB_IO_URL || process.env.SHORT_URL_API || 'https://tgb.io';
        const metadataSuffix = this.element.trackingLinkMetadataRid
          ? `?r=${this.element.trackingLinkMetadataRid}`
          : '';

        return `${baseUrl}/t/${this.element.trackingLinkRid}${metadataSuffix}`;
      }

      if (this.isMapped('permalink')) {
        const link = this.getDataValue(this.element.id, 'permalink');

        if (isURL(link)) {
          return link;
        }

        return '';
      }

      return this.element.permalink;
    },

    logoUrl() {
      return this.element.logoUrl;
    },
  },

  watch: {
    backgroundColor() {
      this.makeQRCode();
    },

    dotColor() {
      this.makeQRCode();
    },

    logoUrl() {
      this.makeQRCode();
    },

    'element.data.dataValue.text': {
      handler(val) {
        if (isURL(val)) {
          this.makeQRCode();
        } else {
          this.$refs.qrcode.innerHTML = '';
        }
      },
    },

    permalink(val) {
      if (this.isMapped('permalink')) {
        if (isURL(val)) {
          this.makeQRCode();
        } else {
          this.$refs.qrcode.innerHTML = '';
        }
      }

      this.updatePermalink();
    },
  },

  created() {
    // this event rerenders the QR code image so it is the right res for it's size
    this.onDraggableResized = throttle(() => {
      if (this.isSelected) this.resize();
    }, 500);
    EventBus.on('draggable-resized', this.onDraggableResized);
  },

  mounted() {
    this.makeQRCode();
  },

  beforeUnmount() {
    EventBus.off('draggable-resized', this.onDraggableResized);
  },

  methods: {
    makeQRCode() {
      if (!this.permalink) { return; }

      const options = {
        text: this.permalink,
        width: this.element.styles.width,
        height: this.element.styles.height,
        colorDark: this.element.dotColor,
        colorLight: this.element.backgroundColor,
        dotScale: 1,
        correctLevel: EasyQRCode.CorrectLevel.H,
        eyeFrameDraw(ctx, x, y, w, h, position) {
          /* eslint-disable no-param-reassign */

          w = (w / 7) * 6;
          h = w;
          ctx.beginPath();
          const r = w / 7;
          ctx.arc(x + r, y + r, r, Math.PI, (Math.PI * 3) / 2);
          ctx.lineTo(w - r + x, y);

          if (position === EasyQRCode.EYE_POSITION.TR) {
            ctx.arc(w - r + x, r + y, r, (Math.PI * 3) / 2, Math.PI * 2);
            ctx.lineTo(w + x, h + y - r);
            ctx.arc(w - r + x, h - r + y, r, 0, (Math.PI * 1) / 2);
            ctx.lineTo(x, h + y);
            ctx.lineTo(x, y + r);
          } else if (position === EasyQRCode.EYE_POSITION.BL) {
            ctx.lineTo(w + x, y);
            ctx.arc(w - r + x, h - r + y, r, 0, (Math.PI * 1) / 2);
            ctx.lineTo(r + x, h + y);
            ctx.arc(r + x, h - r + y, r, (Math.PI * 1) / 2, Math.PI);
            ctx.lineTo(x, y + r);
          } else {
            ctx.arc(w - r + x, r + y, r, (Math.PI * 3) / 2, Math.PI * 2);
            ctx.lineTo(w + x, h + y);
            ctx.lineTo(r + x, h + y);
            ctx.arc(r + x, h - r + y, r, (Math.PI * 1) / 2, Math.PI);
            ctx.lineTo(x, y + r);
          }
          ctx.stroke();
        },
        eyeBallDraw(ctx, x, y, w) {
          const r = w / 6;
          const d = r * 2;
          for (let i = 0; i < 3; i += 1) {
            ctx.beginPath();
            for (let j = 0; j < 3; j += 1) {
              ctx.arc(x + j * d + r, y + i * d + r, r, 0, 2 * Math.PI);
            }
            ctx.fill();
          }
        },
        bodyBlockDraw(ctx, x, y, w, h, blockInfo) {
          w -= 2;
          const r = w / 2;
          x += 1;

          if (blockInfo.type === EasyQRCode.BLOCK.BLANK) {
            ctx.fillRect(x, y, w, h);
          } else if (blockInfo.type === EasyQRCode.BLOCK.SINGLE) {
            ctx.beginPath();
            ctx.arc(x + w / 2, y + h / 2, r, 0, 2 * Math.PI);
            ctx.fill();

            if (blockInfo.alignment === 'I') {
              ctx.fillStyle = this.dotColor;
              ctx.beginPath();
              ctx.arc(x + w / 2, y + h / 2, r, 0, 2 * Math.PI);
              ctx.fill();
            } else {
              ctx.beginPath();
              ctx.arc(x + w / 2, y + h / 2, r, 0, 2 * Math.PI);
              ctx.fill();
            }
          } else if (blockInfo.type === EasyQRCode.BLOCK.END) {
            const direction = blockInfo.endDirection;

            if (direction === EasyQRCode.DIRECTION.UP) {
              ctx.fillRect(x, y, w, h - r);
              ctx.beginPath();
              ctx.arc(x + w / 2, y + h / 2, r, 0, Math.PI);
              ctx.fill();
            } else if (direction === EasyQRCode.DIRECTION.DOWN) {
              ctx.fillRect(x, y + r, w, h - r);
              ctx.beginPath();
              ctx.arc(x + w / 2, y + h / 2, r, Math.PI, 2 * Math.PI);
              ctx.fill();
            } else if (direction === EasyQRCode.DIRECTION.LEFT) {
              ctx.beginPath();
              ctx.arc(x + w / 2, y + h / 2, r, 0, 2 * Math.PI);
              ctx.fill();
            } else if (direction === EasyQRCode.DIRECTION.RIGHT) {
              ctx.beginPath();
              ctx.arc(x + w / 2, y + h / 2, r, 0, 2 * Math.PI);
              ctx.fill();
            }
            ctx.fill();
          } else if (blockInfo.around.up && blockInfo.around.down) {
            ctx.fillRect(x, y, w, h);
          } else if (!blockInfo.around.up && blockInfo.around.down) {
            ctx.fillRect(x, y + r, w, h - r);
            ctx.beginPath();
            ctx.arc(x + w / 2, y + h / 2, r, Math.PI, 2 * Math.PI);
            ctx.fill();
          } else if (!blockInfo.around.down && blockInfo.around.up) {
            ctx.fillRect(x, y, w, h - r);
            ctx.beginPath();
            ctx.arc(x + w / 2, y + h / 2, r, 0, Math.PI);
            ctx.fill();
          } else {
            ctx.beginPath();
            ctx.arc(x + w / 2, y + h / 2, r, 0, 2 * Math.PI);
            ctx.fill();
          }
        },
      };

      if (this.element.hasLogo) {
        options.logo = this.element.logoUrl;
        options.logoBackgroundTransparent = true;
        options.logoBackgroundColor = '#fff';
      }

      if (this.$refs.qrcode) {
        this.$refs.qrcode.innerHTML = '';
        this.qrcode = new EasyQRCode(this.$refs.qrcode, options);
      }
    },

    updatePermalink() {
      if (this.qrcode) {
        this.qrcode.makeCode(this.permalink);
      }
    },

    resize() {
      if (this.qrcode) {
        this.qrcode.resize(
          this.element.styles.width,
          this.element.styles.height,
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.qrcode-widget {
  overflow: visible;
}
</style>
